// Synchronises hover state across all dashboard Highcharts instances so a hover
// on one graph shows matching crosshairs and tooltip values on the others.

// Returns the closest visible point in a series for a given x-axis timestamp.
const isSyncableSeries = (series) => {
  if (!series) return false

  return !(
    series.options?.isInternal === true ||
    series.userOptions?.isInternal === true ||
    /^Navigator\b/.test(series.name || '') ||
    /^highcharts-navigator-series/.test(series.options?.id || '')
  )
}

const getNearestSeriesPoint = (series, hoverX) => {
  if (!isSyncableSeries(series) || !series.visible || series.options.enableMouseTracking === false || !series.points || !series.points.length) {
    return null
  }

  let nearestPoint = null
  let nearestDistance = Infinity

  series.points.forEach((point) => {
    if (!point || typeof point.x !== 'number' || point.isNull) return

    const distance = Math.abs(point.x - hoverX)
    if (distance < nearestDistance) {
      nearestPoint = point
      nearestDistance = distance
    }
  })

  return nearestPoint
}

// Chooses the best anchor point for a chart from all hoverable series.
const getAnchorPointForChart = (chart, hoverX) => {
  let anchorPoint = null
  let nearestDistance = Infinity

  chart.series.forEach((series) => {
    const point = getNearestSeriesPoint(series, hoverX)
    if (!point) return

    const distance = Math.abs(point.x - hoverX)
    if (distance < nearestDistance) {
      anchorPoint = point
      nearestDistance = distance
    }
  })

  return anchorPoint
}

// Collects same-timestamp points so shared tooltips show all series values.
const getPointsForSharedTooltip = (chart, anchorPoint) => {
  if (!chart || !anchorPoint) return []

  return chart.series.reduce((points, series) => {
    if (!isSyncableSeries(series)) return points

    const matchingPoint = series.points && series.points.find((point) => point && point.x === anchorPoint.x && !point.isNull)
    if (matchingPoint) points.push(matchingPoint)
    return points
  }, [])
}

// Builds a small controller for binding, syncing, resetting, and unbinding
// dashboard hover interactions without keeping the logic inside Dashboard.js.
export const createDashboardHoverSync = ({ getCharts }) => {
  // Clears tooltip, crosshair, and hover styling for a single chart.
  const resetChartHover = (chart) => {
    if (!chart) return

    if (chart.hoverPoints && chart.hoverPoints.length) {
      chart.hoverPoints.forEach((point) => point.setState())
    }

    if (chart.hoverPoint) chart.hoverPoint.setState()
    if (chart.tooltip) chart.tooltip.hide(0)
    if (chart.xAxis && chart.xAxis[0]) chart.xAxis[0].hideCrosshair()

    chart.hoverPoints = null
    chart.hoverPoint = null
  }

  // Clears hover state across all currently mounted dashboard charts.
  const reset = () => {
    getCharts().forEach((chart) => resetChartHover(chart))
  }

  // Applies synced hover styling and tooltip content to one chart.
  const syncChartHover = (chart, hoverX) => {
    const anchorPoint = getAnchorPointForChart(chart, hoverX)

    if (!anchorPoint) {
      resetChartHover(chart)
      return
    }

    const tooltipPoints = getPointsForSharedTooltip(chart, anchorPoint)
    const pointsToHighlight = tooltipPoints.length ? tooltipPoints : [anchorPoint]

    resetChartHover(chart)
    pointsToHighlight.forEach((point) => point.setState('hover'))
    chart.hoverPoint = anchorPoint
    chart.hoverPoints = pointsToHighlight
    chart.xAxis[0].drawCrosshair(null, anchorPoint)
    chart.tooltip.refresh(pointsToHighlight)
  }

  // Converts a pointer event on one chart into a shared timestamp and syncs all charts.
  const syncFromEvent = (sourceChart, originalEvent) => {
    if (!sourceChart || !sourceChart.pointer || !sourceChart.xAxis || !sourceChart.xAxis[0]) return

    const normalizedEvent = sourceChart.pointer.normalize(originalEvent)
    const hoverX = sourceChart.xAxis[0].toValue(normalizedEvent.chartX)

    if (typeof hoverX !== 'number' || Number.isNaN(hoverX)) return

    getCharts().forEach((chart) => syncChartHover(chart, hoverX))
  }

  // Attaches hover listeners to each mounted chart once.
  const bind = () => {
    getCharts().forEach((chart) => {
      if (chart.dashboardHoverSyncCleanup) return

      const moveHandler = (event) => syncFromEvent(chart, event)
      const leaveHandler = () => reset()

      chart.container.addEventListener('mousemove', moveHandler)
      chart.container.addEventListener('touchmove', moveHandler)
      chart.container.addEventListener('mouseleave', leaveHandler)
      chart.container.addEventListener('touchend', leaveHandler)
      chart.container.addEventListener('touchcancel', leaveHandler)

      chart.dashboardHoverSyncCleanup = () => {
        chart.container.removeEventListener('mousemove', moveHandler)
        chart.container.removeEventListener('touchmove', moveHandler)
        chart.container.removeEventListener('mouseleave', leaveHandler)
        chart.container.removeEventListener('touchend', leaveHandler)
        chart.container.removeEventListener('touchcancel', leaveHandler)
        delete chart.dashboardHoverSyncCleanup
      }
    })
  }

  // Removes any previously attached hover listeners from mounted charts.
  const unbind = () => {
    getCharts().forEach((chart) => {
      if (chart.dashboardHoverSyncCleanup) chart.dashboardHoverSyncCleanup()
    })
  }

  return {
    bind,
    reset,
    unbind,
  }
}
